/*
  Shared adhesive pack maths.

  The adhesive sells in 1 / 2 / 4 tube packs and the multipacks are cheaper per
  tube. Both surfaces that offer it — the product buy box
  (assets/adhesive-addon.js) and the cart drawer upsell
  (assets/cart-adhesive-upsell.js) — let the customer pick a TUBE COUNT, so
  both need the same answer to "what is the cheapest set of packs reaching N
  tubes?".

  One copy is the point. Two solvers would drift the moment pack sizes or
  prices change, and the drawer would quote a different price than the product
  page for the same tube count.

  Pack objects come from snippets/adhesive-pack-data.liquid and look like
  { id, tubes, price, max, title } — price in cents, max === -1 meaning
  "Shopify is not enforcing a stock ceiling".

  Loaded from sections/header.liquid: header-group renders ahead of
  content_for_layout in layout/theme.liquid and defer scripts execute in
  document order, so this is defined before either consumer runs.
*/
window.AdhesivePacks = window.AdhesivePacks || (function () {
  /* Below this, the multipack saving isn't worth drawing attention to. */
  const MIN_SAVING_CENTS = 500;

  /**
   * Cheapest set of packs totalling at least `tubes`.
   *
   * Exhaustive rather than greedy: greedy assumes bigger packs are always
   * better value, which only holds while multipacks stay discounted. With
   * three pack sizes the search is tiny, so we solve it properly and stay
   * correct whatever the final prices are.
   *
   * Returns { lines: [{id, tubes, price, max, title, qty}], tubes, cost }
   * or null when no pack is purchasable.
   */
  function cheapestCombo(variants, tubes) {
    const packs = (variants || []).filter((v) => v.tubes > 0 && v.price >= 0);
    if (!packs.length) return null;

    const want = Math.max(1, parseInt(tubes, 10) || 1);

    /* best[n] = cheapest way to reach AT LEAST n tubes */
    const best = [{ cost: 0, lines: [] }];

    for (let n = 1; n <= want; n++) {
      let winner = null;
      for (const p of packs) {
        const prev = best[Math.max(0, n - p.tubes)];
        if (!prev) continue;

        /* Respect per-variant stock caps (max === -1 means untracked). */
        const already = prev.lines.filter((l) => l.id === p.id).reduce((s, l) => s + l.qty, 0);
        if (p.max !== -1 && already + 1 > p.max) continue;

        const cost = prev.cost + p.price;
        if (!winner || cost < winner.cost) {
          winner = { cost: cost, lines: prev.lines.concat([Object.assign({}, p, { qty: 1 })]) };
        }
      }
      /* Nothing can reach n (stock exhausted) — stop and use what we have. */
      if (!winner) break;
      best[n] = winner;
    }

    const solution = best[want] || best[best.length - 1];
    if (!solution || !solution.lines.length) return null;

    /* Collapse repeated packs into single lines with a qty. */
    const merged = [];
    solution.lines.forEach((l) => {
      const hit = merged.find((m) => m.id === l.id);
      if (hit) hit.qty += 1;
      else merged.push(Object.assign({}, l));
    });

    return {
      lines: merged,
      tubes: merged.reduce((s, l) => s + l.tubes * l.qty, 0),
      cost: merged.reduce((s, l) => s + l.price * l.qty, 0)
    };
  }

  /* Total tubes actually purchasable, for capping a stepper so the add can
     never fail on the adhesive. Infinity when any pack is untracked. */
  function maxTubes(variants) {
    const packs = variants || [];
    if (!packs.length) return Infinity;
    if (packs.some((p) => p.max === -1)) return Infinity;
    return packs.reduce((s, p) => s + p.tubes * p.max, 0);
  }

  /**
   * What the same tube count would cost bought as singles — a real
   * purchasable alternative rather than a notional RRP, which is what makes
   * the strikethrough honest. That honesty is the whole point, so a
   * single-tube pack that is out of stock does not count: striking through a
   * price nobody can pay is exactly the fake-RRP move this avoids.
   *
   * Returns 0 when there is no purchasable single-tube pack.
   */
  function singlesBaseline(variants, tubes) {
    const single = (variants || []).find((v) => v.tubes === 1 && (v.max === -1 || v.max > 0));
    return single ? single.price * Math.max(1, parseInt(tubes, 10) || 1) : 0;
  }

  /**
   * Saving worth showing, or 0. Trivial savings are suppressed — a loudly
   * displayed $0.90 reads worse than showing nothing at all.
   */
  function displaySaving(variants, tubes, cost) {
    const baseline = singlesBaseline(variants, tubes);
    const saving = baseline - cost;
    return saving >= MIN_SAVING_CENTS ? saving : 0;
  }

  /* The theme's own helper. Note the format lives at
     theme.shopSettings.moneyFormat (snippets/js-variables.liquid) — NOT
     theme.moneyFormat, which has never existed. */
  function formatMoney(cents) {
    const format = window.theme && theme.shopSettings && theme.shopSettings.moneyFormat;
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* Parse a JSON data-attribute without letting a malformed one take the
     whole component down. */
  function parse(json, fallback) {
    try {
      return JSON.parse(json || '');
    } catch (e) {
      return fallback;
    }
  }

  return {
    MIN_SAVING_CENTS: MIN_SAVING_CENTS,
    cheapestCombo: cheapestCombo,
    maxTubes: maxTubes,
    singlesBaseline: singlesBaseline,
    displaySaving: displaySaving,
    formatMoney: formatMoney,
    parse: parse
  };
})();
